import { useEffect, useMemo } from 'react';
import { Tree } from 'react-arborist';
import type { MouseEvent, VoidFunctionComponent } from 'react';
import type { NodeApi, NodeRendererProps } from 'react-arborist';

import { FileTreeNode } from '../../../../generic/FileTree';
import { findFirstFilePath } from '../../../../generic/FileTree/buildTreeUtils/treeUtils';
import useNestingLines from '../../../../generic/FileTree/hooks/useNestingLines';
import type { RevisionFileMatch } from '../../../../../hooks/api/revisions/useFileMatchesForRevision';

import buildTreeFromMatches from './buildTree';
import type { MatchTreeNode } from './buildTree';

import styles from './MatchFileTree.module.less';

const FALLBACK_TREE_HEIGHT = 800;

type Props = {
  matches: RevisionFileMatch[];
  selectedMatch: string | null;
  setSelectedMatch: (match: string | null) => void;
  width?: number;
  height?: number;
};

const MatchFileTreeNode: VoidFunctionComponent<NodeRendererProps<MatchTreeNode>> = ({
  node,
  style,
}) => {
  const isDirectory = node.data.type === 'directory';

  //  Handle clicking on the entire row.
  const handleSelectRow = (event: MouseEvent) => {
    //  Handle selecting a node.
    node.handleClick(event);
  };

  const handleToggleDirectory = () => {
    //  "isInternal" is a react-arborist-ism for "this is a directory".
    //  Calling `node.toggle` will toggle the open/closed state of these nodes.
    if (node.isInternal) {
      node.toggle();
    }
  };

  // Generate nesting lines background for the current node level
  const nestingLinesBackgroundStyles = useNestingLines(node.level);
  const enhancedStyle = useMemo(() => ({
    ...style,
    ...nestingLinesBackgroundStyles,
  }), [nestingLinesBackgroundStyles, style]);

  return (
    <FileTreeNode
      style={enhancedStyle}
      onClick={handleSelectRow}
      isSelected={node.isSelected}
      isDirectory={isDirectory}
    >
      <FileTreeNode.Arrow
        isOpen={node.isOpen}
        isDirectory={isDirectory}
        onToggleDirectory={handleToggleDirectory}
      />
      {!isDirectory && (<FileTreeNode.Icon />)}
      <FileTreeNode.Text text={node.data.name} />
    </FileTreeNode>
  );
};

const MatchFileTree: VoidFunctionComponent<Props> = ({ matches, selectedMatch, setSelectedMatch, width, height }) => {
  //  Memoized since this is a somewhat expensive task (with larger trees)
  const data = useMemo(() => buildTreeFromMatches(matches), [matches]);

  //  Auto-select the first file path when the tree is initially rendered or when snippet changes
  useEffect(() => {
    if (data.length > 0) {
      const firstFilePath = findFirstFilePath(data);
      if (firstFilePath) {
        setSelectedMatch(firstFilePath);
      }
    }
  }, [data, setSelectedMatch]);

  return (
    <Tree<MatchTreeNode>
      // ref={treeRef}
      data={data}
      openByDefault
      idAccessor="path" // Tells it we want to use the "path" as the ID
      childrenAccessor="children" // Tells it we want to use the "children" as the children
      //  Layout
      className={styles.tree}
      rowClassName={styles.row}
      rowHeight={32}
      indent={8}
      width={width}
      height={height ?? FALLBACK_TREE_HEIGHT}
      selection={selectedMatch || undefined}
      onSelect={(nodes: Array<NodeApi<MatchTreeNode>>) => {
        // react-arborist allows multi-select, which is why it passes us an array of nodes.
        // Since multi-select is disabled, we should only receive an array with a single node in it,
        // so we just grab it.
        const node = nodes[0];
        //  Only allow files to be selected.
        if (node?.data?.type === 'file') {
          // We slice off the leading slash because our match paths don't have it, but the tree does.
          setSelectedMatch(node.data.path);
        }
      }}
      disableMultiSelection
      disableDrag
    >
      {MatchFileTreeNode}
    </Tree>
  );
};

export default MatchFileTree;
